/** A dry-run build (re-couple preview, review preview): per output, whether the next build emits
 *  it (or why not), whether upstream changed since the last import, and which fields it would
 *  OVERWRITE — the current values, ops' edits included. The answer is the integration's
 *  (`preview_catalogue_outputs`); the known shape renders as a table, anything else as JSON. */
import { Alert, Box, Chip, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import type { CataloguePreview } from '@/api/catalogue';

import { entityLabel } from './catalogueModel';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

interface PreviewOutput {
  id?: number;
  entity?: string;
  ext_id?: string;
  in_document?: boolean;
  reason?: string | null;
  changed_upstream?: boolean;
  overwrites?: string[] | null;
}

function asOutputs(v: unknown): PreviewOutput[] | null {
  return Array.isArray(v) ? (v.filter((x) => x && typeof x === 'object') as PreviewOutput[]) : null;
}

export function PreviewResult({ preview }: { preview: CataloguePreview }) {
  const body = preview.preview as Record<string, unknown>;
  const outputs = asOutputs(body.outputs);
  const omitted = Array.isArray(body.omitted) ? body.omitted : [];
  const notes = Array.isArray(body.notes) ? (body.notes as unknown[]).map(String) : [];
  if (!outputs) {
    return (
      <Box component="pre" sx={{ fontFamily: MONO, fontSize: 12, whiteSpace: 'pre-wrap', m: 0 }}>
        {JSON.stringify(body, null, 2)}
      </Box>
    );
  }
  return (
    <Box data-testid="catalogue-preview">
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 0.5 }}>
        A dry run — nothing was written.
      </Typography>
      <Table size="small" aria-label="Preview">
        <TableHead>
          <TableRow>
            <TableCell>Output</TableCell>
            <TableCell>Next build</TableCell>
            <TableCell>Would overwrite</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {outputs.map((o, i) => (
            <TableRow key={o.id ?? i}>
              <TableCell>
                <Typography variant="body2">{entityLabel(o.entity ?? '')}</Typography>
                <Typography variant="caption" sx={{ fontFamily: MONO, color: 'text.secondary', wordBreak: 'break-all' }}>
                  {o.ext_id}
                </Typography>
              </TableCell>
              <TableCell>
                {o.in_document ? 'rebuilt' : `left out${o.reason ? ` — ${o.reason}` : ''}`}
                {o.changed_upstream && (
                  <Chip size="small" label="changed upstream" color="warning" variant="outlined" sx={{ ml: 0.5, height: 20, fontSize: 11 }} />
                )}
              </TableCell>
              <TableCell>
                {o.overwrites === null || o.overwrites === undefined ? (
                  <Typography variant="body2" color="text.secondary">
                    — (not in the database yet)
                  </Typography>
                ) : o.overwrites.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    nothing — matches what is there
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {o.overwrites.map((f) => (
                      <Chip key={f} size="small" label={f} color="error" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                    ))}
                  </Box>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {omitted.length > 0 && (
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
          Omitted: {omitted.map((x) => (typeof x === 'object' ? JSON.stringify(x) : String(x))).join(', ')}
        </Typography>
      )}
      {notes.map((n) => (
        <Alert key={n} severity="info" sx={{ mt: 0.5, py: 0 }}>
          {n}
        </Alert>
      ))}
    </Box>
  );
}
